/**
 * Utility functions for consistent status styling across the app
 */

/**
 * Get Tailwind CSS classes for status badges
 * @param status - The status string (e.g., 'ENABLED', 'PAUSED', 'Enabled', 'Paused')
 * @returns Tailwind CSS classes for the status badge
 */
export function getStatusBadgeClasses(status: string | undefined): string {
  if (!status) {
    return 'bg-gray-100 text-gray-800';
  }

  const normalizedStatus = status.toUpperCase();

  // Pending states with animation (check these first to avoid matching ENABLED/PAUSED)
  if (normalizedStatus === 'ENABLING') {
    return 'bg-green-100 text-green-800 animate-pulse';
  }

  if (normalizedStatus === 'PAUSING') {
    return 'bg-amber-100 text-amber-800 animate-pulse';
  }

  if (normalizedStatus === 'REMOVING') {
    return 'bg-red-100 text-red-800 animate-pulse';
  }

  // Standard states
  if (normalizedStatus.includes('ENABLED')) {
    return 'bg-green-100 text-green-800';
  }

  if (normalizedStatus.includes('PAUSED')) {
    return 'bg-amber-100 text-amber-800';
  }

  if (normalizedStatus.includes('REMOVED')) {
    return 'bg-red-100 text-red-800';
  }

  // Default for unknown statuses
  return 'bg-gray-100 text-gray-800';
}

/**
 * Get Tailwind CSS classes for status text (without background)
 * @param status - The status string (e.g., 'ENABLED', 'PAUSED', 'Enabled', 'Paused')
 * @returns Tailwind CSS classes for the status text color
 */
export function getStatusTextClasses(status: string | undefined): string {
  if (!status) {
    return 'text-gray-500';
  }

  const normalizedStatus = status.toUpperCase();

  if (normalizedStatus.includes('ENABLED')) {
    return 'text-green-600';
  }

  if (normalizedStatus.includes('PAUSED')) {
    return 'text-amber-600';
  }

  if (normalizedStatus.includes('REMOVED')) {
    return 'text-red-600';
  }

  // Default for unknown statuses
  return 'text-gray-500';
}
